import React, { useState, useEffect } from "react";
import { doc, getDoc, setDoc, updateDoc } from "firebase/firestore";
import {
  Box,
  Tabs,
  Tab,
  TextField,
  Button,
  Typography,
  Table,
  TableBody,
  TableRow,
  TableCell,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  InputLabel,
  Paper,
  TableContainer,
} from "@mui/material";
import { v4 as uuidv4 } from 'uuid';
import { db } from "../firebase";

export default function PennantSettings() {
  const [activeTab, setActiveTab] = useState(0);
  const [settings, setSettings] = useState({});
  const [newSeason, setNewSeason] = useState({ name: "", startDate: "", endDate: "" });
  const [newScoringType, setNewScoringType] = useState({
    name: "",
    servesPerRally: "",
    pointsPerGame: "",
    gamesPerSet: "",
    setsPerMatch: "",
    useSets: false,
    scoringSystem: "",
    pointsCalculation: "",
    matchFormat: "",
    tieBreakerRule: "",
  });
  const [newPlayType, setNewPlayType] = useState({ name: "" });
  const [newAgeGroup, setNewAgeGroup] = useState({ name: "" });
  const [newSection, setNewSection] = useState({ name: "" });
  const [newPennantDay, setNewPennantDay] = useState({ name: "" });
  const [newParticipantType, setNewParticipantType] = useState({ type: "Individuals" });

  // State for total courts
  const [totalCourtsValue, setTotalCourtsValue] = useState("");

  useEffect(() => {
    const fetchSettings = async () => {
      const settingsRef = doc(db, "pennantSettings", "settingsDoc");
      const settingsDoc = await getDoc(settingsRef);
      if (settingsDoc.exists()) {
        setSettings(settingsDoc.data());
      } else {
        // Create an empty doc if it doesn't exist
        await setDoc(settingsRef, {});
      }
    };
    fetchSettings();
  }, []);

  // Whenever settings changes, sync totalCourtsValue if available
  useEffect(() => {
    if (settings.totalCourts !== undefined) {
      setTotalCourtsValue(settings.totalCourts);
    }
  }, [settings]);

  // Update an array field in settings
  const updateSettings = async (field, value) => {
    const settingsRef = doc(db, "pennantSettings", "settingsDoc");
    const updatedSettings = {
      ...settings,
      [field]: [...(settings[field] || []), value],
    };
    await updateDoc(settingsRef, updatedSettings);
    setSettings(updatedSettings);
  };

  // Update the single numeric "totalCourts" field
  const handleSaveTotalCourts = async () => {
    const settingsRef = doc(db, "pennantSettings", "settingsDoc");
    const updatedSettings = {
      ...settings,
      totalCourts: parseInt(totalCourtsValue, 10) || 0,
    };
    await updateDoc(settingsRef, updatedSettings);
    setSettings(updatedSettings);
  };

  // Handlers for adding array-based settings
  const handleAddParticipantType = () =>
    updateSettings("participantTypes", { id: uuidv4(), type: newParticipantType.type });

  const handleAddSeason = () =>
    updateSettings("seasons", { id: uuidv4(), ...newSeason });

  const handleAddScoringType = () =>
    updateSettings("scoringTypes", { id: uuidv4(), ...newScoringType });

  const handleAddPlayType = () =>
    updateSettings("playTypes", { id: uuidv4(), name: newPlayType.name });

  const handleAddAgeGroup = () =>
    updateSettings("ageGroups", { id: uuidv4(), name: newAgeGroup.name });

  const handleAddSection = () =>
    updateSettings("sections", { id: uuidv4(), name: newSection.name });

  const handleAddPennantDay = () =>
    updateSettings("pennantDays", { id: uuidv4(), name: newPennantDay.name });

  // Tab render functions
  const renderSeasonsTab = () => (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>Manage Seasons</Typography>
      <TextField
        label="Season Name"
        value={newSeason.name}
        onChange={(e) => setNewSeason({ ...newSeason, name: e.target.value })}
        fullWidth
        margin="normal"
      />
      <TextField
        label="Start Date"
        type="date"
        value={newSeason.startDate}
        onChange={(e) => setNewSeason({ ...newSeason, startDate: e.target.value })}
        fullWidth
        margin="normal"
      />
      <TextField
        label="End Date"
        type="date"
        value={newSeason.endDate}
        onChange={(e) => setNewSeason({ ...newSeason, endDate: e.target.value })}
        fullWidth
        margin="normal"
      />
      <Button variant="contained" color="primary" onClick={handleAddSeason} sx={{ mt: 2 }}>
        Add Season
      </Button>
      <TableContainer component={Paper} sx={{ mt: 3 }}>
        <Table>
          <TableBody>
            {(settings.seasons || []).map((season) => (
              <TableRow key={season.id}>
                <TableCell>{season.name}</TableCell>
                <TableCell>{season.startDate}</TableCell>
                <TableCell>{season.endDate}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  const renderScoringTypesTab = () => (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>Manage Scoring Types</Typography>
      <TextField
        label="Scoring Type Name"
        value={newScoringType.name}
        onChange={(e) => setNewScoringType({ ...newScoringType, name: e.target.value })}
        fullWidth
        margin="normal"
      />
      <TextField
        label="Serves per Rally"
        value={newScoringType.servesPerRally}
        onChange={(e) => setNewScoringType({ ...newScoringType, servesPerRally: e.target.value })}
        fullWidth
        margin="normal"
      />
      <TextField
        label="Points per Game"
        value={newScoringType.pointsPerGame}
        onChange={(e) => setNewScoringType({ ...newScoringType, pointsPerGame: e.target.value })}
        fullWidth
        margin="normal"
      />
      <TextField
        label={newScoringType.useSets ? "Games per Set" : "Games per Match"}
        value={newScoringType.gamesPerSet}
        onChange={(e) => setNewScoringType({ ...newScoringType, gamesPerSet: e.target.value })}
        fullWidth
        margin="normal"
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={newScoringType.useSets}
            onChange={(e) => setNewScoringType({ ...newScoringType, useSets: e.target.checked })}
          />
        }
        label="Use Sets"
      />
      {newScoringType.useSets && (
        <TextField
          label="Sets per Match"
          value={newScoringType.setsPerMatch}
          onChange={(e) => setNewScoringType({ ...newScoringType, setsPerMatch: e.target.value })}
          fullWidth
          margin="normal"
        />
      )}
      <FormControl fullWidth margin="normal">
        <InputLabel id="scoring-system-label">Scoring System</InputLabel>
        <Select
          labelId="scoring-system-label"
          value={newScoringType.scoringSystem}
          onChange={(e) => setNewScoringType({ ...newScoringType, scoringSystem: e.target.value })}
          label="Scoring System"
        >
          <MenuItem value="Point-a-Rally">Point-a-Rally</MenuItem>
          <MenuItem value="Handouts">Handouts</MenuItem>
        </Select>
      </FormControl>
      <FormControl fullWidth margin="normal">
        <InputLabel id="match-format-label">Match Format</InputLabel>
        <Select
          labelId="match-format-label"
          value={newScoringType.matchFormat}
          onChange={(e) => setNewScoringType({ ...newScoringType, matchFormat: e.target.value })}
          label="Match Format"
        >
          <MenuItem value="Best of">Best of</MenuItem>
          <MenuItem value="Play All">Play All Games</MenuItem>
        </Select>
      </FormControl>
      <FormControl fullWidth margin="normal">
        <InputLabel id="points-calculation-label">Match Points Calculation</InputLabel>
        <Select
          labelId="points-calculation-label"
          value={newScoringType.pointsCalculation}
          onChange={(e) => setNewScoringType({ ...newScoringType, pointsCalculation: e.target.value })}
          label="Match Points Calculation"
        >
          <MenuItem value="Per Game">Points per Game</MenuItem>
          <MenuItem value="Per Match">Points per Match</MenuItem>
          <MenuItem value="Per Rally">Points per Rally</MenuItem>
        </Select>
      </FormControl>
      <FormControl fullWidth margin="normal">
        <InputLabel id="tie-breaker-label">Tiebreaker Rule</InputLabel>
        <Select
          labelId="tie-breaker-label"
          value={newScoringType.tieBreakerRule}
          onChange={(e) => setNewScoringType({ ...newScoringType, tieBreakerRule: e.target.value })}
          label="Tiebreaker Rule"
        >
          <MenuItem value="Win by 2">Win by 2</MenuItem>
          <MenuItem value="First to X">First to Points per Game</MenuItem>
        </Select>
      </FormControl>
      <Button
        variant="contained"
        color="primary"
        onClick={handleAddScoringType}
        sx={{ mt: 2 }}
      >
        Add Scoring Type
      </Button>
      <TableContainer component={Paper} sx={{ mt: 3 }}>
        <Table>
          <TableBody>
            {(settings.scoringTypes || []).map((type) => (
              <TableRow key={type.id}>
                <TableCell>{type.name}</TableCell>
                <TableCell>{type.servesPerRally}</TableCell>
                <TableCell>{type.pointsPerGame}</TableCell>
                <TableCell>{type.gamesPerSet}</TableCell>
                <TableCell>{type.setsPerMatch || "N/A"}</TableCell>
                <TableCell>{type.scoringSystem}</TableCell>
                <TableCell>{type.matchFormat}</TableCell>
                <TableCell>{type.pointsCalculation}</TableCell>
                <TableCell>{type.tieBreakerRule}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  const renderPlayTypesTab = () => (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>Manage Play Types</Typography>
      <TextField
        label="Play Type Name"
        value={newPlayType.name}
        onChange={(e) => setNewPlayType({ ...newPlayType, name: e.target.value })}
        fullWidth
        margin="normal"
      />
      <Button variant="contained" color="primary" onClick={handleAddPlayType} sx={{ mt: 2 }}>
        Add Play Type
      </Button>
      <TableContainer component={Paper} sx={{ mt: 3 }}>
        <Table>
          <TableBody>
            {(settings.playTypes || []).map((type) => (
              <TableRow key={type.id}>
                <TableCell>{type.name}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  const renderAgeGroupsTab = () => (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>Manage Age Groups</Typography>
      <TextField
        label="Age Group Name"
        value={newAgeGroup.name}
        onChange={(e) => setNewAgeGroup({ ...newAgeGroup, name: e.target.value })}
        fullWidth
        margin="normal"
      />
      <Button variant="contained" color="primary" onClick={handleAddAgeGroup} sx={{ mt: 2 }}>
        Add Age Group
      </Button>
      <TableContainer component={Paper} sx={{ mt: 3 }}>
        <Table>
          <TableBody>
            {(settings.ageGroups || []).map((group) => (
              <TableRow key={group.id}>
                <TableCell>{group.name}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  const renderSectionsTab = () => (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>Manage Sections</Typography>
      <TextField
        label="Section Name"
        value={newSection.name}
        onChange={(e) => setNewSection({ ...newSection, name: e.target.value })}
        fullWidth
        margin="normal"
      />
      <Button variant="contained" color="primary" onClick={handleAddSection} sx={{ mt: 2 }}>
        Add Section
      </Button>
      <TableContainer component={Paper} sx={{ mt: 3 }}>
        <Table>
          <TableBody>
            {(settings.sections || []).map((section) => (
              <TableRow key={section.id}>
                <TableCell>{section.name}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  const renderPennantDaysTab = () => (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>Manage Pennant Days</Typography>
      <TextField
        label="Pennant Day Name"
        value={newPennantDay.name}
        onChange={(e) => setNewPennantDay({ ...newPennantDay, name: e.target.value })}
        fullWidth
        margin="normal"
      />
      <Button variant="contained" color="primary" onClick={handleAddPennantDay} sx={{ mt: 2 }}>
        Add Pennant Day
      </Button>
      <TableContainer component={Paper} sx={{ mt: 3 }}>
        <Table>
          <TableBody>
            {(settings.pennantDays || []).map((day) => (
              <TableRow key={day.id}>
                <TableCell>{day.name}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  const renderParticipantTypesTab = () => (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>Manage Participant Types</Typography>
      <FormControl fullWidth margin="normal">
        <InputLabel id="participant-type-label">Participant Type</InputLabel>
        <Select
          labelId="participant-type-label"
          value={newParticipantType.type}
          onChange={(e) => setNewParticipantType({ type: e.target.value })}
          label="Participant Type"
        >
          <MenuItem value="Individuals">Individuals</MenuItem>
          <MenuItem value="Teams">Teams</MenuItem>
        </Select>
      </FormControl>
      <Button variant="contained" color="primary" onClick={handleAddParticipantType} sx={{ mt: 2 }}>
        Add Participant Type
      </Button>
      <TableContainer component={Paper} sx={{ mt: 3 }}>
        <Table>
          <TableBody>
            {(settings.participantTypes || []).map((type) => (
              <TableRow key={type.id}>
                <TableCell>{type.type}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  // New tab to manage "Total Courts"
  const renderTotalCourtsTab = () => (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>Total Courts</Typography>
      <TextField
        label="Total Courts"
        type="number"
        value={totalCourtsValue}
        onChange={(e) => setTotalCourtsValue(e.target.value)}
        fullWidth
        margin="normal"
      />
      <Button
        variant="contained"
        color="primary"
        onClick={handleSaveTotalCourts}
        sx={{ mt: 2 }}
      >
        Save Total Courts
      </Button>
    </Box>
  );

  return (
    <Box sx={{ minHeight: '500px' }}>
      <Tabs value={activeTab} onChange={(e, newVal) => setActiveTab(newVal)} centered>
        <Tab label="Seasons" />
        <Tab label="Scoring Types" />
        <Tab label="Play Types" />
        <Tab label="Age Groups" />
        <Tab label="Sections" />
        <Tab label="Pennant Days" />
        <Tab label="Participant Types" />
        <Tab label="Total Courts" />
      </Tabs>

      {activeTab === 0 && renderSeasonsTab()}
      {activeTab === 1 && renderScoringTypesTab()}
      {activeTab === 2 && renderPlayTypesTab()}
      {activeTab === 3 && renderAgeGroupsTab()}
      {activeTab === 4 && renderSectionsTab()}
      {activeTab === 5 && renderPennantDaysTab()}
      {activeTab === 6 && renderParticipantTypesTab()}
      {activeTab === 7 && renderTotalCourtsTab()}
    </Box>
  );
}
